/**
 * PATH: apps/quote/src/components/builder/other-charges-section/EditableCell.tsx
 * PURPOSE: Inline-editable cell for the charges table
 */

import { useState } from 'react'
import { cn } from '@/lib/utils'

export function EditableCell({
  value,
  onChange,
  type = 'text',
  placeholder = '',
  readOnly = false,
  align = 'left',
  className = '',
  prefix = '',
  suffix = '',
}: {
  value: string | number | null | undefined
  onChange: (next: string) => void
  type?: 'text' | 'number'
  placeholder?: string
  readOnly?: boolean
  align?: 'left' | 'right' | 'center'
  className?: string
  prefix?: string
  suffix?: string
}) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(String(value ?? ''))

  const commit = () => {
    setIsEditing(false)
    if (String(value ?? '') !== editValue) onChange(editValue)
  }

  if (readOnly) {
    return (
      <div className={cn('px-2 py-1.5 text-xs', align === 'right' && 'text-right', className)}>
        {prefix}{String(value ?? '')}{suffix}
      </div>
    )
  }

  if (isEditing) {
    return (
      <div className="flex items-center">
        {prefix && <span className="text-xs text-slate-500 pl-1">{prefix}</span>}
        <input
          type={type}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit()
            if (e.key === 'Escape') { setEditValue(String(value ?? '')); setIsEditing(false) }
          }}
          className={cn(
            'w-full px-1.5 py-1 text-xs border-2 border-blue-500 bg-blue-50 dark:bg-blue-900/20 outline-none rounded',
            align === 'right' && 'text-right', className
          )}
          step={type === 'number' ? '0.01' : undefined}
          autoFocus
        />
        {suffix && <span className="text-xs text-slate-500 pr-1">{suffix}</span>}
      </div>
    )
  }

  return (
    <div className={cn('px-2 py-1.5 text-xs cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/10 rounded', align === 'right' && 'text-right', className)} onClick={() => setIsEditing(true)}>
      {prefix}{String(value ?? '') || placeholder}{suffix}
    </div>
  )
}
